import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Compass, Shield, Settings, TrendingUp, CheckCircle, Thermometer, Layers, Box } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const Print3DMethodology = () => {
  const focuses = [
    {
      icon: Shield,
      title: 'Minimizing print failures and material waste',
      description: 'Prioritizing reliable first layers and consistent extrusion over speed'
    },
    {
      icon: Thermometer,
      title: 'Working within real printer limitations',
      description: 'Understanding your hotend, bed, and enclosure capabilities—not ideal specs'
    },
    {
      icon: Layers,
      title: 'Supporting desktop and prosumer printers',
      description: 'Tailored guidance for real-world FDM and resin machines'
    },
    {
      icon: TrendingUp,
      title: 'Encouraging iterative calibration',
      description: 'Progress through test prints, temperature towers, and flow adjustments—not one-shot perfection'
    }
  ];

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.METHODOLOGY)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
    <div className="max-w-4xl mx-auto space-y-8 p-4" data-testid="3dp-methodology-page">
      {/* Page Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 border border-orange-500/50 rounded-sm bg-orange-500/10">
          <Compass className="w-5 h-5 text-orange-500" />
          <span className="text-sm font-bold text-orange-500 uppercase tracking-wide">Our Approach</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Our Methodology
        </h1>
      </div>

      {/* Main Content Card */}
      <Card className="bg-[#242424] border-[#3a3a3a]">
        <CardContent className="pt-6 space-y-6">
          {/* Introduction */}
          <div className="prose prose-invert max-w-none">
            <p className="text-base sm:text-lg text-[#d6d6d6] leading-relaxed">
              3D Printing Helpdesk is designed around how 3D printers are <span className="text-orange-500 font-semibold">actually built and used</span> — not how they appear in marketing materials or ideal specifications.
            </p>
            
            <p className="text-[#a3a3a3] leading-relaxed mt-4">
              The system asks structured, practical questions about your printer configuration, filament properties, and project goals. Where information is missing or unclear, 3D Printing Helpdesk applies conservative, experience-based defaults that prioritize print success and material efficiency.
            </p>
          </div>

          {/* Recommendations Focus */}
          <div className="mt-8">
            <h2 className="text-lg font-bold text-white uppercase tracking-wide mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Recommendations Focus On:
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {focuses.map((focus, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm hover:border-orange-500/30 transition-colors"
                >
                  <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <focus.icon className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{focus.title}</h3>
                    <p className="text-[#a3a3a3] text-xs">{focus.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Experience Statement */}
          <div className="mt-8 p-5 bg-[#1f1f1f] border-l-4 border-orange-500 rounded-r-sm">
            <p className="text-[#d6d6d6] leading-relaxed">
              This approach reflects <span className="text-white font-semibold">hands-on 3D printing experience</span>, countless calibration prints, material experiments, and real-world troubleshooting — where reliable prints are achieved through testing, adjustment, and patience.
            </p>
          </div>

          {/* Closing Statement */}
          <div className="mt-6 flex items-start gap-3 p-4 bg-orange-500/5 border border-orange-500/20 rounded-sm">
            <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-[#d6d6d6]">
              <span className="text-white font-semibold">3D Printing Helpdesk provides a reliable starting point</span> that users can adapt as their printers, materials, and skills evolve.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
};

export default Print3DMethodology;
